import { h } from 'preact'
import clsx from 'clsx'

interface PromolineData {
  title: string
  body?: string
  cta?: {
    text?: string
    url?: string
  }
  text_colour: string
  background_colour?: string
  priority: number
  end_date?: string
  countdown?: string[]
}

interface PromolineProps {
  promoline: PromolineData
  promolineLocation?: string
  promolineSize?: 'small' | 'medium' | 'large'
  title?: string
  body?: string
  cta_text?: string
  cta_url?: string
  text_colour?: string
  bg_colour?: string
  end_date?: string
  countdown?: string[]
}

export const Promoline = ({
  promoline,
  promolineLocation,
  promolineSize,
  title: titleOverride,
  body: bodyOverride,
  cta_text: ctaTextOverride,
  cta_url: ctaUrlOverride,
  text_colour: textColourOverride,
  bg_colour: bgColourOverride,
  end_date: endDateOverride,
  countdown: countdownOverride,
  className
}: PromolineProps & { className?: string }) => {
  if (!promoline || !promoline.title) return null

  const title = titleOverride ?? promoline.title
  const body = bodyOverride ?? promoline.body
  const ctaText = ctaTextOverride ?? promoline.cta?.text
  const ctaUrl = ctaUrlOverride ?? promoline.cta?.url
  const textColour = textColourOverride ?? promoline.text_colour
  const bgColour = bgColourOverride ?? promoline.background_colour
  const endDate = endDateOverride ?? promoline.end_date
  const countdown = countdownOverride ?? promoline.countdown

  const countdownString = Array.isArray(countdown) ? countdown.join(',') : ''

  const shouldShowCountdown = (() => {
    if (!endDate || !countdownString || !promolineLocation) return false
    return countdownString.includes(promolineLocation)
  })()

  const getSizeConfig = () => {
    switch (promolineSize) {
      case 'small':
        return {
          container: 'w-fit',
          containerBg: '',
          titleClass: 'text-xs leading-none font-semibold uppercase',
          countdownLayout: 'small',
          countdownPosition: 'inline'
        }
      case 'medium':
        return {
          container: 'w-fit bg-[--bg-colour]',
          containerBg: shouldShowCountdown ? 'pl-0.5 pr-0.125 py-0.125 rounded-80 overflow-hidden' : 'px-0.5 py-0.375 rounded-80 overflow-hidden',
          titleClass: 'text-xs leading-none font-semibold uppercase',
          countdownLayout: 'small',
          countdownPosition: 'inline'
        }
      case 'large': {
        const hasBodyOrCta = !!(body || ctaText)
        let largeContainerBg = shouldShowCountdown
          ? 'pl-1 pr-0.125 py-0.125 overflow-hidden'
          : 'px-1 py-0.5 overflow-hidden'

        if (hasBodyOrCta) {
          largeContainerBg += ' rounded-24'
          if (shouldShowCountdown) {
            largeContainerBg += ' !px-1 !py-0.5'
          }
        } else {
          largeContainerBg += ' rounded-80'
        }

        return {
          container: 'w-full bg-[--bg-colour]',
          containerBg: largeContainerBg,
          titleClass: 'paragraph-s font-semibold uppercase',
          countdownLayout: 'small',
          countdownPosition: 'block'
        }
      }
    }
  }

  const sizeConfig = getSizeConfig()
  const Component = ctaUrl ? 'a' : 'div'

  const componentProps = ctaUrl ? {
    href: ctaUrl,
    'aria-label': title
  } : {}

  const CountdownTimer = ({
    endDate,
    layout = 'small'
  }: {
    endDate: string
    layout?: 'small' | 'medium' | 'large'
  }) => {
    if (!endDate) return null

    const getLayoutConfig = () => {
      switch (layout) {
        case 'small':
          return {
            wrapperClass: 'hidden group-data-visible/countdown:flex gap-x-0.125',
            itemClass: 'flex',
            numbersClass: 'text-xs leading-none font-semibold',
            textClass: 'text-xs leading-none font-semibold'
          }
        case 'medium':
          return {
            wrapperClass: 'hidden group-data-visible/countdown:flex gap-x-1 children:shrink-0',
            itemClass: 'flex items-end gap-x-0.25',
            numbersClass: 'text-xs leading-none font-semibold',
            textClass: 'text-xs leading-none font-semibold'
          }
        case 'large':
          return {
            wrapperClass: 'hidden group-data-visible/countdown:flex gap-x-1.25 children:shrink-0',
            itemClass: 'flex items-end gap-x-0.25',
            numbersClass: 'h3 font-semibold',
            textClass: 'h6'
          }
      }
    }

    const config = getLayoutConfig()
    const labels = {
      small: ['d,', 'h,', 'm,', 's'],
      medium: ['days', 'hrs', 'mins', 'secs'],
      large: ['days', 'hrs', 'mins', 'secs']
    }
    const labelArray = labels[layout]

    return h(
      'x-countdown',
      {
        'data-end-date': new Date(endDate).toISOString(),
        'data-ui': 'visible',
        className: 'group/countdown shrink-0'
      },
      h('div', { className: config.wrapperClass },
        h('div', { className: config.itemClass },
          h('span', { 'data-countdown-days': true, className: config.numbersClass }, '00'),
          h('span', { className: config.textClass }, labelArray[0])
        ),
        h('div', { className: config.itemClass },
          h('span', { 'data-countdown-hours': true, className: config.numbersClass }, '00'),
          h('span', { className: config.textClass }, labelArray[1])
        ),
        h('div', { className: config.itemClass },
          h('span', { 'data-countdown-minutes': true, className: config.numbersClass }, '00'),
          h('span', { className: config.textClass }, labelArray[2])
        ),
        h('div', { className: config.itemClass },
          h('span', { 'data-countdown-seconds': true, className: config.numbersClass }, '00'),
          h('span', { className: config.textClass }, labelArray[3])
        )
      )
    )
  }

  return (
    <Component
      {...componentProps}
      className={clsx(
        'shrink-0 flex',
        !shouldShowCountdown && 'flex-wrap',
        'items-center justify-between gap-x-0.5 gap-y-0.25',
        sizeConfig.container,
        'h-fit text-[--text-colour]',
        bgColour && sizeConfig.containerBg,
        ctaUrl && 'link-fade',
        className
      )}
      style={{
        '--text-colour': textColour,
        '--bg-colour': bgColour || 'transparent'
      } as React.CSSProperties}
    >
      <div className={clsx(
        'grow flex flex-wrap items-center gap-x-0.5 gap-y-0.25',
        sizeConfig.container
      )}>
        {sizeConfig.countdownPosition === 'inline' ? (
          <div className={clsx(
            'flex items-center gap-x-0.5',
            sizeConfig.countdownPosition === 'block' ? 'shrink-0' : 'flex-wrap'
          )}>
            {title && (
              <span className={sizeConfig.titleClass}>
                {title}
              </span>
            )}
            {shouldShowCountdown && (
              <div className="text-brand-black bg-brand-white px-0.5 py-0.25 rounded-80">
                <CountdownTimer
                  endDate={endDate}
                  layout={sizeConfig.countdownLayout as 'small' | 'medium' | 'large'}
                />
              </div>
            )}
          </div>
        ) : (
          <>
            {(title || body) && (
              <div className="grow flex flex-wrap items-center gap-x-0.5 gap-y-0.25">
                {title && (
                  <span className={sizeConfig.titleClass}>
                    {title}
                  </span>
                )}
                {body && (
                  <span className="grow paragraph-xs">
                    {body}
                  </span>
                )}
              </div>
            )}
          </>
        )}

        {ctaText && promolineSize === 'large' && (
          <span className="shrink-0 size-fit text-line text-xs">
            {ctaText}
          </span>
        )}
      </div>

      {shouldShowCountdown && sizeConfig.countdownPosition === 'block' && (
        <div className="text-brand-black bg-brand-white px-0.5 py-0.25 rounded-80">
          <CountdownTimer
            endDate={endDate}
            layout={sizeConfig.countdownLayout as 'small' | 'medium' | 'large'}
          />
        </div>
      )}
    </Component>
  )
}
